import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Typography,
	Checkbox,
	FormControlLabel,
	Grid,
	Switch,
} from "@mui/material";

import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../../store/actions";
import { getBugsAssigneeData, jiraChartResponseProfile } from "../../../Service/Apis";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../../store/Store";
import { useDispatch, useSelector } from "react-redux";
import MonthButtonGroup from "../../utilization/MonthsButtonGroup";
import { useCustomToast } from "../../Methods/SnackBar";
import BarChartComponent from "../../charts/BarChartComponent";
import {
	barChartSeriesNames,
	jiraChartBlobDownload,
	resolveJiraExportEmployeeIds,
} from "../chartExport";
import { JiraEmployeeBarTooltip } from "../../utilization/utils";
import { isEmployeeAccessRole } from "../../../utils/accessRole";

export type BugsAssigneeChartProps = {
	employeeId?: string;
	controlledMonth?: string;
	hideMonthButtons?: boolean;
	tooltipScopedSeriesName?: string;
};

const BugsAssigneeChart = (props: BugsAssigneeChartProps = {}) => {
	const { employeeId, controlledMonth, hideMonthButtons, tooltipScopedSeriesName } =
		props;
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();
	const [bugsReportData, setBugsReportData] = useState<any>([]);
	const [teamName, setTeamName] = useState<string>("");
	const [month, setMonth] = useState<string>("1");
	const [selectedFormat, setSelectedFormat] = useState<string>("excel");
	const showToast = useCustomToast();
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [selectAll, setSelectAll] = useState(true);

	const nrole = useSelector((state: any) => state.auth.selectedRole);
	const paramsData = useMemo(
		() => ({
			...jiraChartResponseProfile,
			role: nrole ?? "Employee",
			...(employeeId ? { employee_ids: [employeeId] } : {}),
		}),
		[nrole, employeeId]
	);

	const fetchBugsReportData = async (selectedMonth: string) => {
		try {
			setLoading(true);
			const res = await dispatch2(
				fetchData(`${getBugsAssigneeData}${selectedMonth}`, paramsData)
			);
			setMonth(selectedMonth);
			if (res.data) {
				setBugsReportData(res.data);
			} else {
				setBugsReportData(res);
			}

			// Set all employee names as selected by default
			const allEmployeeNames = res?.teams
				? res?.teams?.flatMap(
						(issue: any) =>
							issue.employees?.map((assignee: any) => assignee.employee_name) ||
							[]
				  ) || []
				: res?.data?.flatMap((issue: any) => issue.assignee_name || []);
			setSelectedEmployees(allEmployeeNames);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchBugsReportData(controlledMonth ?? "1");
	}, [controlledMonth, employeeId, nrole]);

	useEffect(() => {
		if (employeeId && tooltipScopedSeriesName) {
			setSelectedEmployees([tooltipScopedSeriesName]);
			setSelectAll(true);
		}
	}, [employeeId, tooltipScopedSeriesName, bugsReportData]);

	useEffect(() => {
		if (!employeeId || !bugsReportData?.teams?.length) return;
		const t = bugsReportData.teams.find((x: any) => x.team_name === "All");
		if (t) setTeamName("All");
	}, [employeeId, bugsReportData]);

	useEffect(() => {
		if (!bugsReportData?.teams?.length) return;
		const selectedTeam =
			bugsReportData.teams.find((team: any) => team.team_name === teamName) ||
			bugsReportData.teams[0];
		if (!selectedTeam) return;

		if (!teamName) {
			setTeamName(selectedTeam.team_name);
		}

		const teamEmployeeNames =
			selectedTeam.employees?.map((e: any) => e.employee_name) ?? [];
		if (
			selectedEmployees.length === 0 ||
			!selectedEmployees.every((name) => teamEmployeeNames.includes(name))
		) {
			setSelectedEmployees(teamEmployeeNames);
		}
	}, [bugsReportData, teamName, selectedEmployees.length]);

	const handleMonthChange = (selectedMonth: string) => {
		fetchBugsReportData(selectedMonth);
	};

	const buttonData = [
		{ label: "1M", onClick: () => handleMonthChange("1") },
		{ label: "3M", onClick: () => handleMonthChange("3") },
		{ label: "6M", onClick: () => handleMonthChange("6") },
		{ label: "1Y", onClick: () => handleMonthChange("12") },
	];

	const computedData = useMemo(() => {
		// Handle data for employee role (second format)
		if (Array.isArray(bugsReportData?.issues || bugsReportData)) {
			const sourceData = bugsReportData?.issues || bugsReportData;
			const data = sourceData?.flatMap((issue: any) =>
				issue.assignees?.length > 0
					? issue.assignees.map((assignee: any) => ({
							assignee_name: assignee?.assignee_name,
							week: issue?.week_end,
							issue_count: assignee?.issue_count,
							by_project: assignee?.by_project,
					  }))
					: [
							{
								week: issue?.week_end,
								assignee_name: issue?.assignee_name,
								issue_count: issue?.bug_count,
								by_project: issue?.by_project,
							},
					  ]
			);

			const groupedData = data.reduce((acc: any, curr: any) => {
				let weekData = acc.find((item: any) => item.week === curr.week);
				if (!weekData) {
					weekData = { week: curr.week, __jiraTooltip: {} };
					acc.push(weekData);
				}
				if (!weekData.__jiraTooltip) weekData.__jiraTooltip = {};
				weekData[curr.assignee_name] = curr.issue_count;
				weekData.__jiraTooltip[curr.assignee_name] = {
					by_project: curr.by_project || [],
				};
				return acc;
			}, []);

			return groupedData;
		}

		// Handle data for other roles (first format with teams)
		if (!bugsReportData || !bugsReportData.teams) {
			return [];
		}

		const selectedTeam =
			bugsReportData.teams.find((team: any) => team.team_name === teamName) ||
			bugsReportData.teams[0];

		setTeamName(selectedTeam?.team_name);

		if (selectedTeam) {
			const data = selectedTeam.weeks.flatMap((week: any) =>
				week?.assignee?.length > 0
					? week.assignee.map((assignee: any) => ({
							assignee_name: assignee?.assignee_name,
							week: week?.week_end,
							issue_count: assignee?.bug_count,
							by_project: assignee?.by_project,
					  }))
					: [
							{
								week: week?.week_end,
								assignee_name: "No Assignees",
								issue_count: 0,
								by_project: [],
							},
					  ]
			);

			const groupedData = data.reduce((acc: any, curr: any) => {
				let weekData = acc.find((item: any) => item.week === curr.week);
				if (!weekData) {
					weekData = { week: curr.week, __jiraTooltip: {} };
					acc.push(weekData);
				}
				if (!weekData.__jiraTooltip) weekData.__jiraTooltip = {};
				weekData[curr.assignee_name] = curr.issue_count;
				weekData.__jiraTooltip[curr.assignee_name] = {
					by_project: curr.by_project || [],
				};
				return acc;
			}, []);

			return groupedData;
		}
		return [];
	}, [bugsReportData, teamName]);

	const chartEmployeeNames = useMemo(
		() => barChartSeriesNames(computedData),
		[computedData]
	);

	const onDownload = async () => {
		try {
			setDownloading(true);
			const isEmployeeRole =
				(nrole ?? "Employee").toLowerCase() === "employee";

			let selectedTeam: any = null;
			if (!isEmployeeRole) {
				if (!teamName) {
					throw new Error("Please select a team");
				}
				selectedTeam = bugsReportData.teams?.find(
					(team: any) => team.team_name === teamName
				);
				if (!selectedTeam) {
					throw new Error("Selected team not found");
				}
			}

			const employeeIds = resolveJiraExportEmployeeIds({
				selectedEmployees,
				chartEmployeeNames,
				teamEmployees: selectedTeam?.employees,
				rowsForIdFallback: [],
			});

			const baseParams: Record<string, unknown> = {
				...paramsData,
				download: selectedFormat,
			};
			if (employeeId) {
				baseParams.employee_ids = [employeeId];
			} else if (employeeIds) {
				baseParams.employee_ids = employeeIds;
			}
			if (!isEmployeeRole && selectedTeam) {
				baseParams.team_id = selectedTeam.team_id;
			}

			const ext =
				selectedFormat === "excel" ? "xlsx" : selectedFormat;
			const filename = !isEmployeeRole
				? `bugs-assignee-${teamName.replace(/\s+/g, "-")}.${ext}`
				: `bugs-assignee.${ext}`;

			await jiraChartBlobDownload(
				`${getBugsAssigneeData}${month}`,
				baseParams,
				filename,
				showToast
			);
		} catch (error: any) {
			showToast(error?.message ?? String(error), "error", true);
		} finally {
			setDownloading(false);
		}
	};

	// Helper function to get all employee names
	const getAllEmployeeNames = () => {
		return bugsReportData?.teams
			? bugsReportData?.teams?.flatMap((issue: any) =>
					issue.employees.map((assignee: any) => assignee.employee_name)
			  ) || []
			: bugsReportData?.issues?.flatMap((issue: any) =>
					issue.assignees.map((assignee: any) => assignee.assignee_name)
			  ) || [];
	};

	// Update handleEmployeeSelection to manage selectAll state
	const handleEmployeeSelection = (employeeName: string) => {
		const newSelectedEmployees = selectedEmployees.includes(employeeName)
			? selectedEmployees.filter((name) => name !== employeeName)
			: [...selectedEmployees, employeeName];

		setSelectedEmployees(newSelectedEmployees);

		const uniqueEmployees = barChartSeriesNames(computedData);

		if (newSelectedEmployees.length === uniqueEmployees.length) {
			setSelectAll(true);
		}
		if (newSelectedEmployees.length === 0) {
			setSelectAll(false);
		}
	};

	// Replace handleSwitchChange with simpler logic
	const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setSelectAll(checked);

		if (checked) {
			// Select all employees
			setSelectedEmployees(getAllEmployeeNames());
		} else {
			// Deselect all employees
			setSelectedEmployees([]);
		}
	};

	return (
		<>
			<Paper sx={{ m: 2, p: 4 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						<Typography variant="h4">Bugs Assignee Chart</Typography>
						{!hideMonthButtons && (
							<MonthButtonGroup buttonData={buttonData} />
						)}
					</Box>
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						gap={2}
					>
						{bugsReportData?.teams && !employeeId && (
							<FormControl
								size="small"
								sx={{ minWidth: 220, width: "100%", maxWidth: 320 }}
							>
								<InputLabel id="bugs-assignee-team-select-label">
									Select Team
								</InputLabel>
								<Select
									labelId="bugs-assignee-team-select-label"
									id="bugs-assignee-team-select"
									value={teamName}
									label="Select Team"
									onChange={(e) => {
										setTeamName(e.target.value);
									}}
								>
									{bugsReportData?.teams?.map((team: any) => (
										<MenuItem key={team.team_id} value={team.team_name}>
											{team.team_name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}

						<Box
							display="flex"
							justifyContent="center"
							gap={2}
							alignItems="center"
							flexWrap="wrap"
						>
							<FormControl size="small" sx={{ minWidth: 120 }}>
								<InputLabel id="bugs-assignee-export-format-label">
									Format
								</InputLabel>
								<Select
									labelId="bugs-assignee-export-format-label"
									label="Format"
									value={selectedFormat}
									onChange={(e) =>
										setSelectedFormat(e.target.value)
									}
								>
									<MenuItem value="excel">
										Excel (.xlsx)
									</MenuItem>
									<MenuItem value="csv">CSV</MenuItem>
									<MenuItem value="json">JSON</MenuItem>
								</Select>
							</FormControl>
							{downloading ? (
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									gap={2}
								>
									<CircularProgress size="1rem" />
									<Typography>Downloading...</Typography>
								</Box>
							) : (
								<Button
									variant="outlined"
									size="small"
									onClick={onDownload}
								>
									Export
								</Button>
							)}
						</Box>
					</Box>
				</Box>
				{!employeeId && !isEmployeeAccessRole(nrole) && (
					<Box
						display="flex"
						justifyContent="flex-end"
						alignItems="center"
						gap={2}
						m={2}
						width="100%"
					>
						<Typography>Select All Employees</Typography>
						<Switch
							checked={selectAll}
							onChange={handleSwitchChange}
							color="primary"
						/>
					</Box>
				)}
				{loading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						width="100%"
						height={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
					>
						<BarChartComponent
							data={computedData || []}
							YAxisLabel="Issue Count"
							selectedEmployees={selectedEmployees}
							onEmployeeSelection={handleEmployeeSelection}
							tooltipContent={
								<JiraEmployeeBarTooltip
									scopedSeriesName={tooltipScopedSeriesName}
								/>
							}
						/>
					</Box>
				)}
			</Paper>
		</>
	);
};

export default BugsAssigneeChart;
